import React, { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import useMediaQuery from "./useMediaQuery";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

function EmptyState() {
  return (
    <div
      style={{
        height: 160,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px dashed rgba(255,255,255,0.12)",
        borderRadius: 14,
        color: "#8b9296",
        fontSize: 13,
        margin: "16px 0",
      }}
    >
      No data found
    </div>
  );
}

export default function AdminTable({ columns, rows = [], minWidth = 560 }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const total = rows.length;
  const pageRows = rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const rangeStart = total === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, total);
  const canPrev = page > 0;
  const canNext = rangeEnd < total;

  const handleRowsPerPage = (n) => {
    setRowsPerPage(n);
    setPage(0);
  };

  if (!rows || rows.length === 0) return <EmptyState />;

  const pagination = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: 8,
        padding: "12px 16px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        background: "rgba(255,255,255,0.01)",
      }}
    >
      <span style={{ fontSize: 12, color: "#8b9296", marginRight: 4 }}>
        {isMobile ? "Rows:" : "Rows per page:"}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 6,
              color: "#fff",
              fontSize: 12,
              fontWeight: 600,
              padding: "4px 8px",
              cursor: "pointer",
            }}
          >
            {rowsPerPage}
            <ChevronDown size={12} color="#8b9296" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" style={{ minWidth: 64 }}>
          {ROWS_PER_PAGE_OPTIONS.map((n) => (
            <DropdownMenuItem key={n} onSelect={() => handleRowsPerPage(n)}>
              {n}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span style={{ fontSize: 12, color: "#8b9296", margin: "0 8px" }}>
        {rangeStart}–{rangeEnd} of {total}
      </span>
      <button
        type="button"
        disabled={!canPrev}
        onClick={() => setPage(page - 1)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 28, height: 28, borderRadius: 7,
          background: "transparent", border: "1px solid rgba(255,255,255,0.08)",
          color: canPrev ? "#fff" : "rgba(255,255,255,0.25)",
          cursor: canPrev ? "pointer" : "not-allowed",
        }}
      >
        <ChevronLeft size={15} />
      </button>
      <button
        type="button"
        disabled={!canNext}
        onClick={() => setPage(page + 1)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 28, height: 28, borderRadius: 7,
          background: "transparent", border: "1px solid rgba(255,255,255,0.08)",
          color: canNext ? "#fff" : "rgba(255,255,255,0.25)",
          cursor: canNext ? "pointer" : "not-allowed",
        }}
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ borderRadius: 14, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden", width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", padding: 10, gap: 10 }}>
          {pageRows.map((row, index) => {
            const primaryValue = columns[0]?.render ? columns[0].render(row) : row[columns[0]?.key];
            return (
              <div
                key={row.id ?? index}
                style={{
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.018)",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", background: "rgba(255,255,255,0.03)" }}>
                  <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.08em", color: "#8b9296", textTransform: "uppercase" }}>
                    {columns[0]?.label}
                  </span>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", marginTop: 2 }}>{primaryValue}</div>
                </div>
                <div style={{ padding: "8px 12px 12px" }}>
                  {columns.slice(1).map((col) => (
                    <div
                      key={col.key || col.label}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(90px, 36%) minmax(0, 1fr)",
                        gap: 10,
                        padding: "6px 0",
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontSize: 9, fontWeight: 900, letterSpacing: "0.08em", color: "#8b9296", textTransform: "uppercase" }}>
                        {col.label}
                      </div>
                      <div
                        style={{
                          minWidth: 0,
                          fontSize: 12.5,
                          color: "#c3c8cc",
                          textAlign: "right",
                          overflowWrap: "anywhere",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        {col.render ? col.render(row) : row[col.key]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {pagination}
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: 0,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", minWidth, width: "100%" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {columns.map((col) => (
                <th
                  key={col.key || col.label}
                  style={{
                    textAlign: col.align || "left",
                    padding: "11px 14px",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    color: "#8b9296",
                    whiteSpace: "nowrap",
                    background: "rgba(255,255,255,0.015)",
                    width: col.width,
                  }}
                >
                  {typeof col.label === "string" ? col.label.toUpperCase() : col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, index) => (
              <tr
                key={row.id ?? index}
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.02)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {columns.map((col) => (
                  <td
                    key={col.key || col.label}
                    style={{
                      padding: "11px 14px",
                      fontSize: 13,
                      color: "#c3c8cc",
                      textAlign: col.align || "left",
                      verticalAlign: "middle",
                      whiteSpace: col.wrap ? "normal" : "nowrap",
                      maxWidth: col.maxWidth,
                      overflow: col.maxWidth ? "hidden" : undefined,
                      textOverflow: col.maxWidth ? "ellipsis" : undefined,
                    }}
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination}
    </div>
  );
}
